<script lang="ts">
  // Shared quick-add form. Unlike the sidebar flow it collects the name
  // alongside the address, so neither picking a suggestion nor dropping a pin
  // needs the follow-up name prompt.
  import { addSheet, placementHouseholdSize, placementMode, placementName, friendPlacement, mapView, showToast, startPlacement } from '../lib/state';
  import { householdSize } from '../lib/household';
  import { geocode } from '../lib/api';
  import { addTripByAddress, addCandidateByAddress } from '../lib/entities';
  import { parseListingUrl } from '../lib/address';
  import type { Prediction } from '../lib/types';
  import AddressInput from './AddressInput.svelte';

  const COPY = {
    friend: { title: 'Add friend(s)', nameLabel: 'Name(s)', namePlaceholder: 'e.g. Sam & Naomi' },
    trip: { title: 'Add a place', nameLabel: 'Name', namePlaceholder: 'e.g. Tennis courts' },
    candidate: { title: 'Add a home to compare', nameLabel: 'Nickname (optional)', namePlaceholder: 'e.g. Castro garden flat' },
  } as const;

  let name = '';
  let peopleCount = 1;
  let address = '';
  let listingUrl = '';
  let busy = false;
  let lookupVersion = 0;
  let addrRef: AddressInput;

  $: kind = $addSheet;
  $: copy = kind ? COPY[kind] : null;

  // Clear the form whenever the sheet opens (or switches to another kind).
  let lastKind: typeof kind = null;
  $: if (kind !== lastKind) {
    lastKind = kind;
    name = '';
    peopleCount = 1;
    address = '';
    listingUrl = '';
    busy = false;
  }

  function close() {
    lookupVersion++;
    addSheet.set(null);
  }

  async function submit(prediction: Prediction | null) {
    const value = address.trim();
    if (!value || busy || !kind) return;
    busy = true;
    const requestVersion = lookupVersion;
    const selectedKind = kind;
    try {
      if (kind === 'friend') {
        const location = await geocode(value);
        if (requestVersion !== lookupVersion) return;
        startPlacement('friend', name);
        placementHouseholdSize.set(householdSize(peopleCount));
        friendPlacement.set({ lat: location.lat, lng: location.lng });
        mapView.set({ lat: location.lat, lng: location.lng, zoom: 15 });
        close();
        return;
      }
      const opts = { name, skipPrompt: true };
      const added = kind === 'trip'
          ? await addTripByAddress(value, prediction, opts)
          : await addCandidateByAddress(value, prediction, opts);
      if (added && requestVersion === lookupVersion) {
        placementMode.set(selectedKind);
        placementName.set(null);
        close();
      }
    } catch {
      showToast('Could not find that location. Try including the city.');
    } finally {
      busy = false;
    }
  }

  async function importListing() {
    const url = listingUrl.trim();
    if (busy) return;
    if (!parseListingUrl(url)) {
      showToast('Paste a Zillow or Redfin link');
      return;
    }
    busy = true;
    try {
      const added = await addCandidateByAddress(url, null, { name, skipPrompt: true });
      if (added) {
        placementMode.set('candidate');
        placementName.set(null);
        close();
      }
    } catch {
      showToast('Could not find that listing');
    } finally {
      busy = false;
    }
  }

  function dropPin() {
    if (!kind) return;
    startPlacement(kind, name);
    if (kind === 'friend') placementHouseholdSize.set(householdSize(peopleCount));
    close();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && kind) {
      e.stopPropagation();
      close();
    }
  }
</script>

<svelte:window on:keydown={onKeydown} />

{#if kind && copy}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div class="add-sheet-backdrop" on:click={close} aria-hidden="true"></div>

  <div class="add-sheet" role="dialog" aria-modal="true" aria-labelledby="add-sheet-title">
    <h2 class="add-sheet-title" id="add-sheet-title">{copy.title}</h2>

    {#if kind === 'candidate'}
      <p class="friend-location-help">Considering a house or apartment? Start with its listing.</p>
      <label class="add-sheet-label" for="home-listing-url">Paste a Zillow or Redfin link</label>
      <div class="add-sheet-url-row">
        <input id="home-listing-url" type="url" bind:value={listingUrl} placeholder="https://www.zillow.com/…" autocomplete="off" aria-label="Zillow or Redfin listing URL" on:keydown={(event) => { if (event.key === 'Enter') importListing(); }} />
        <button class="add-sheet-import" on:click={importListing} disabled={busy || !listingUrl.trim()}>{busy ? 'Adding…' : 'Add listing'}</button>
      </div>
      <details class="custom-place-label">
        <summary>Add a nickname (optional)</summary>
        <label for="home-nickname">Nickname</label>
        <input id="home-nickname" bind:value={name} placeholder="e.g. Sunny corner apartment" autocomplete="off" />
      </details>
    {/if}

    {#if kind === 'friend'}
    <label for="add-sheet-name">{copy.nameLabel}</label>
    <input
      id="add-sheet-name"
      type="text"
      bind:value={name}
      placeholder={copy.namePlaceholder}
      autocomplete="off"
    />

    {/if}

    {#if kind === 'friend'}
      <details class="custom-place-label">
        <summary>More than one friend lives here? (optional)</summary>
        <label for="friend-household-size">Number of friends</label>
        <input id="friend-household-size" type="number" min="1" max="99" step="1" bind:value={peopleCount} />
      </details>
      <h3 class="friend-location-title">Where do they live?</h3>
      <p class="friend-location-help">A rough location is fine. Put the pin wherever you’re comfortable. Travel times will be estimated from there.</p>
      <button class="add-sheet-pin primary-pin" on:click={dropPin} disabled={busy}>📍 Drop a pin on the map</button>
    {/if}
    <span class="add-sheet-label">{kind === 'friend' ? 'Or search an address or approximate location' : kind === 'trip' ? 'Search for a place' : 'Or search an address'}</span>
    <AddressInput
      bind:this={addrRef}
      bind:value={address}
      nameOnly={kind !== 'friend'}
      placeholder={kind === 'friend' ? 'Address, intersection, landmark, or neighborhood' : 'Search an address or place'}
      ariaLabel={kind === 'friend' ? 'Address or approximate location' : 'Address'}
      on:select={(e) => { if (kind !== 'trip') submit(e.detail.prediction); }}
      on:submit={() => submit(addrRef.getPrediction())}
    />

    {#if kind === 'trip'}
      <p class="friend-location-help">We’ll use the name from your Google search result.</p>
      <details class="custom-place-label">
        <summary>Add a custom label (optional)</summary>
        <label for="place-custom-label">Custom label</label>
        <input id="place-custom-label" bind:value={name} placeholder="e.g. My favorite coffee shop" autocomplete="off" />
      </details>
      <button class="place-submit" on:click={() => submit(addrRef.getPrediction())} disabled={busy || !address.trim()}>{busy ? 'Adding…' : 'Add place'}</button>
    {/if}

    {#if kind === 'friend'}
      <p class="friend-location-help">An intersection, nearby landmark, or neighborhood works too. You can adjust the pin before adding your friend.</p>
      <button class="friend-search" on:click={() => submit(addrRef.getPrediction())} disabled={busy || !address.trim()}>{busy ? 'Finding…' : 'Find on map'}</button>
    {/if}

    {#if kind !== 'friend'}
      <button class="add-sheet-pin" on:click={dropPin}>📍 Drop a pin on the map instead</button>
    {/if}

    <div class="add-sheet-foot">
      <button class="add-sheet-cancel" on:click={close}>Cancel</button>
    </div>
  </div>
{/if}

<style>
  .custom-place-label { margin: 12px 0; font-size: 12px; color: var(--ink-soft); }
  .custom-place-label summary { cursor: pointer; color: var(--accent); }
  .custom-place-label label { margin-top: 10px; }
  .place-submit { width: 100%; margin-top: 8px; }

  .friend-location-title { font-size: 14px; margin: 18px 0 6px; }
  .friend-location-help { font-size: 12px; color: var(--ink-soft); line-height: 1.45; margin: 6px 0 10px; }
  .add-sheet-pin.primary-pin, .add-sheet-pin.primary-pin:hover { background: var(--accent); color: #f5edd9; }
  .friend-search { background: transparent; color: var(--accent); border: 1px solid var(--line); width: 100%; }

  .add-sheet-title {
    font-family: var(--serif);
    font-style: italic;
    font-weight: 400;
    font-size: 25px;
    line-height: 1.1;
    color: var(--ink);
    margin: 0 0 14px;
  }
  .add-sheet-label {
    display: block;
    font-size: 12px;
    color: var(--ink-soft);
    margin: 12px 0 4px;
  }
  .add-sheet-url-row { display: flex; gap: 6px; }
  .add-sheet-url-row input { flex: 1; min-width: 0; }
  .add-sheet-import { flex: none; padding: 0 15px; font-weight: 600; border-radius: 8px; }
  .add-sheet-pin {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    margin-top: 14px;
    padding: 11px;
    background: #fdfaf0;
    border: 1px solid var(--line);
    border-radius: 8px;
    color: var(--ink);
    font-size: 12.5px;
    font-weight: 600;
  }
  .add-sheet-pin:hover { background: #fffdf7; color: var(--ink); }
  .add-sheet-foot { display: flex; justify-content: flex-end; margin-top: 8px; }
  .add-sheet-cancel {
    background: transparent;
    color: var(--ink-soft);
    padding: 8px 16px;
    font-size: 12.5px;
    font-weight: 600;
  }
  .add-sheet-cancel:hover { background: transparent; color: var(--ink); }
</style>
